import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Checkbox, Form, Input, message } from 'antd';
import { ArrowLeftOutlined, LockOutlined, MobileOutlined, UserOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import oturumSaati from '../../utils/oturumSaati';
import GirisKabugu, { Not, sureYaz, useGeriSayim } from './GirisKabugu';
import AltchaKutusu from './AltchaKutusu';
import { AYDINLATMA_BASLIK, AYDINLATMA_MADDELERI } from './aydinlatmaMetni';

/**
 * Giriş (03.11.2026'da iki adımlıya çevrildi; sunucu tarafı routes/login.py).
 *   1) Kullanıcı adı + şifre. 2 hatadan sonra robot doğrulama, üst üste 3 hatada 5 dk bekleme (sunucu söyler).
 *   2) Şifre doğruysa telefona 6 haneli kod; gün boyu geçerli (aynı gün yeniden girişte yeni SMS gitmez).
 * "Beni hatırla" yalnız kullanıcı adını tutar: eskiden şifre de localStorage'a açık metin yazılıyordu.
 */
const KOD_HANE = 6;

const SignInPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, kodDogrula, kodTekrar } = useAuth();
  const [adim, setAdim] = useState('sifre');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [beniHatirla, setBeniHatirla] = useState(false);
  const [not, setNot] = useState(null);
  const [robot, setRobot] = useState(false);
  const [robotYuku, setRobotYuku] = useState(null);
  const [robotNo, setRobotNo] = useState(0);
  const [kilit, kilitBaslat] = useGeriSayim();
  const [tekrar, tekrarBaslat] = useGeriSayim();
  const [iki, setIki] = useState(null);
  const [kod, setKod] = useState('');
  const [aydinlatma, setAydinlatma] = useState(false);
  const kodGirdisi = useRef(null);

  useEffect(() => {
    const parametre = new URLSearchParams(window.location.search);
    const oturumMesaji = oturumSaati.mesajiAl();
    if (oturumMesaji) {
      setNot({ tur: 'uyari', metin: oturumMesaji });
    } else if (parametre.get('logout') === 'true') {
      setNot({ tur: 'basari', metin: 'Güvenli şekilde çıkış yaptınız.' });
    }
    if (parametre.toString()) window.history.replaceState({}, document.title, window.location.pathname);
    localStorage.removeItem('remembered_password');
    const kayitli = localStorage.getItem('remembered_username');
    if (kayitli && localStorage.getItem('remember_me') === 'true') {
      form.setFieldsValue({ username: kayitli });
      setBeniHatirla(true);
    }
  }, [form]);

  useEffect(() => {
    if (adim === 'kod') setTimeout(() => kodGirdisi.current && kodGirdisi.current.focus(), 50);
  }, [adim]);

  // Bekleme bitince "… sonra tekrar deneyin" mesajı ekranda kalmasın.
  const oncekiKilit = useRef(0);
  useEffect(() => {
    if (oncekiKilit.current > 0 && kilit === 0) setNot(null);
    oncekiKilit.current = kilit;
  }, [kilit]);

  const robotuYenile = () => {
    setRobotYuku(null);
    setRobotNo((n) => n + 1);
  };

  const hatirla = (kullaniciAdi) => {
    if (beniHatirla) {
      localStorage.setItem('remembered_username', kullaniciAdi);
      localStorage.setItem('remember_me', 'true');
    } else {
      localStorage.removeItem('remembered_username');
      localStorage.removeItem('remember_me');
    }
  };

  const girisTamam = (kullanici) => {
    message.success(`Hoş geldiniz, ${kullanici.ad_soyad}!`);
    const hedef = location.state && location.state.from && location.state.from.pathname;
    navigate(hedef && hedef !== '/giris' ? hedef : '/home', { replace: true });
  };

  const hataGoster = (s) => {
    if (s.robot_gerekli !== undefined) setRobot(!!s.robot_gerekli);
    if (s.kilit_kalan) kilitBaslat(s.kilit_kalan);
    setNot({ tur: s.status === 429 ? 'uyari' : 'hata', metin: s.error });
  };

  const robotHazirMi = () => {
    if (robot && !robotYuku) {
      setNot({ tur: 'bilgi', metin: 'Robot doğrulaması tamamlanıyor, birkaç saniye sonra tekrar deneyin.' });
      return false;
    }
    return true;
  };

  const sifreGonder = async (degerler) => {
    if (kilit || !robotHazirMi()) return;
    setYukleniyor(true);
    setNot(null);
    const kullaniciAdi = degerler.username.trim();
    const s = await login(kullaniciAdi, degerler.password, robot ? robotYuku : null);
    setYukleniyor(false);
    if (robot) robotuYenile();
    if (s.success) {
      hatirla(kullaniciAdi);
      girisTamam(s.user);
    } else if (s.ikiAdim) {
      hatirla(kullaniciAdi);
      form.setFieldValue('password', '');
      setIki(s);
      setKod('');
      setRobot(!!s.robot_gerekli);
      kilitBaslat(0);
      tekrarBaslat(s.tekrar_bekleme || 0);
      setAdim('kod');
    } else {
      hataGoster(s);
    }
  };

  const geriDon = (mesaj) => {
    setAdim('sifre');
    setIki(null);
    setKod('');
    setRobot(false);
    kilitBaslat(0);
    setNot(mesaj ? { tur: 'uyari', metin: mesaj } : null);
  };

  const kodGonder = async (deger) => {
    const temiz = String(deger !== undefined ? deger : kod).replace(/\D/g, '');
    if (temiz.length !== KOD_HANE) {
      setNot({ tur: 'hata', metin: `Doğrulama kodu ${KOD_HANE} haneli olmalı.` });
      return;
    }
    if (kilit || !robotHazirMi()) return;
    setYukleniyor(true);
    setNot(null);
    const s = await kodDogrula(iki.on_oturum, temiz, robot ? robotYuku : null);
    setYukleniyor(false);
    if (robot) robotuYenile();
    if (s.success) {
      girisTamam(s.user);
      return;
    }
    setKod('');
    if (s.yeniden_giris) {
      geriDon(s.error);
      return;
    }
    if (s.kod_yok) {
      setNot({ tur: 'uyari', metin: s.error });
      return;
    }
    hataGoster(s);
    setTimeout(() => kodGirdisi.current && kodGirdisi.current.focus(), 50);
  };

  const kodDegisti = (e) => {
    const temiz = e.target.value.replace(/\D/g, '').slice(0, KOD_HANE);
    setKod(temiz);
    if (temiz.length === KOD_HANE && !yukleniyor && !kilit && (!robot || robotYuku)) kodGonder(temiz);
  };

  const yenidenGonder = async () => {
    setYukleniyor(true);
    setNot(null);
    const s = await kodTekrar(iki.on_oturum);
    setYukleniyor(false);
    if (s.success) {
      setIki({ ...iki, telefon: s.telefon || iki.telefon, kod_durumu: 'gonderildi' });
      tekrarBaslat(s.tekrar_bekleme || 60);
      setKod('');
      setNot({ tur: 'basari', metin: s.detail });
      if (kodGirdisi.current) kodGirdisi.current.focus();
      return;
    }
    if (s.yeniden_giris) {
      geriDon(s.error);
      return;
    }
    if (s.tekrar_bekleme) tekrarBaslat(s.tekrar_bekleme);
    setNot({ tur: s.status === 429 ? 'uyari' : 'hata', metin: s.error });
  };

  const kilitNotu = kilit > 0 && (
    <Not tur="uyari">
      Çok fazla hatalı deneme yapıldı. Tekrar denemek için <span className="gk-sayac">{sureYaz(kilit)}</span> bekleyin.
    </Not>
  );
  const robotKutusu = robot && !kilit && (
    <AltchaKutusu key={`${adim}-${robotNo}`} onDogrulandi={setRobotYuku} />
  );

  return (
    <GirisKabugu>
      {adim === 'sifre' ? (
        <>
          <h2 className="gk-baslik">Fedora'ya giriş yapın</h2>
          <p className="gk-aciklama">Kullanıcı adınız ve şifrenizle giriş yapın.</p>
          {kilitNotu || (not && <Not tur={not.tur}>{not.metin}</Not>)}
          <Form form={form} name="signin" onFinish={sifreGonder} autoComplete="on" size="large" layout="vertical"
            requiredMark={false}>
            <Form.Item name="username" label="Kullanıcı adı"
              rules={[{ required: true, whitespace: true, message: 'Kullanıcı adınızı girin.' }]}>
              <Input prefix={<UserOutlined />} placeholder="Kullanıcı adınız" autoComplete="username"
                autoCapitalize="none" spellCheck={false} />
            </Form.Item>
            <Form.Item name="password" label="Şifre" rules={[{ required: true, message: 'Şifrenizi girin.' }]}>
              <Input.Password prefix={<LockOutlined />} placeholder="Şifreniz" autoComplete="current-password" />
            </Form.Item>
            <div className="gk-satir">
              <Checkbox checked={beniHatirla} onChange={(e) => setBeniHatirla(e.target.checked)}>
                Kullanıcı adımı hatırla
              </Checkbox>
              <Link className="gk-baglanti" to="/sifre-sifirlama">Şifremi unuttum</Link>
            </div>
            {robotKutusu}
            <Button className="gk-birincil" type="primary" htmlType="submit" loading={yukleniyor}
              disabled={kilit > 0}>
              {kilit > 0 ? `Bekleyin (${sureYaz(kilit)})` : 'Devam et'}
            </Button>
          </Form>
        </>
      ) : (
        <>
          <h2 className="gk-baslik">Doğrulama kodu</h2>
          <p className="gk-aciklama">
            {iki.kod_durumu === 'gonderildi'
              ? <>Telefonunuza {KOD_HANE} haneli bir kod gönderdik. Kod bugün <b>{iki.gecerlilik}</b>'a kadar geçerlidir.</>
              : <>Bugün telefonunuza gönderilen {KOD_HANE} haneli kodu girin. Kod <b>{iki.gecerlilik}</b>'a kadar geçerlidir.</>}
          </p>
          <span className="gk-telefon"><MobileOutlined />{iki.telefon}</span>
          {kilitNotu || (not && <Not tur={not.tur}>{not.metin}</Not>)}
          <form onSubmit={(e) => { e.preventDefault(); kodGonder(); }} autoComplete="on">
            <div className="gk-kod-alani">
              <label htmlFor="gk-kod">Doğrulama kodu</label>
              <Input id="gk-kod" ref={kodGirdisi} className="gk-kod" value={kod} onChange={kodDegisti}
                inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={KOD_HANE}
                placeholder={'•'.repeat(KOD_HANE)} aria-describedby="gk-kod-ipucu" disabled={kilit > 0} />
              <p className="gk-ipucu" id="gk-kod-ipucu">
                Kodu kaybettiyseniz ya da gelmediyse yeni kod isteyebilirsiniz; önceki kod geçersiz olur.
              </p>
            </div>
            {robotKutusu}
            <Button className="gk-birincil" type="primary" htmlType="submit" loading={yukleniyor}
              disabled={kilit > 0 || kod.length !== KOD_HANE}>
              {kilit > 0 ? `Bekleyin (${sureYaz(kilit)})` : 'Doğrula ve giriş yap'}
            </Button>
          </form>
          <div className="gk-alt-satir">
            <button type="button" className="gk-baglanti" onClick={() => geriDon()}>
              <ArrowLeftOutlined /> Farklı hesapla giriş
            </button>
            <button type="button" className="gk-baglanti" onClick={yenidenGonder} disabled={tekrar > 0 || yukleniyor}>
              {tekrar > 0 ? `Yeni kod (${sureYaz(tekrar)})` : 'Yeni kod gönder'}
            </button>
          </div>
          {/* 03.11.2026 KVKK: telefon numarasının kullanımı yalnız bu (şifreden sonraki) adımda anlatılır; metin aydinlatmaMetni.js */}
          <p className="gk-kvkk">
            Telefon numaranız yalnız giriş doğrulaması için kullanılır.{' '}
            <button type="button" className="gk-baglanti" aria-expanded={aydinlatma} aria-controls="gk-aydinlatma"
              onClick={() => setAydinlatma(!aydinlatma)}>
              {aydinlatma ? 'Aydınlatma metnini gizle' : 'Aydınlatma metni'}
            </button>
          </p>
          {aydinlatma && (
            <div className="gk-aydinlatma" id="gk-aydinlatma" role="region" aria-label={AYDINLATMA_BASLIK}>
              <h3>{AYDINLATMA_BASLIK}</h3>
              <dl>
                {AYDINLATMA_MADDELERI.map(([baslik, metin]) => (
                  <React.Fragment key={baslik}><dt>{baslik}</dt><dd>{metin}</dd></React.Fragment>
                ))}
              </dl>
            </div>
          )}
        </>
      )}
    </GirisKabugu>
  );
};

export default SignInPage;
